<template>
  <div class="newsletter-signup">
    <GridContainer>
      <GridItem
        :size-lg="10"
        :offset-lg="1"
        :size-xl="10"
        :offset-xl="1"
        class="newsletter-signup-wrapper"
      >
        <div class="image-container">
          <StoryblokImg
            :image="{
              filename:
                'https://a.storyblok.com/f/223704/233x143/a5bc0cdb08/newsletter_capture.svg',
              alt: 'Newsletter',
            }"
          />
        </div>
        <Transition name="fade" mode="out-in">
          <div
            class="newsletter-signup-form"
            v-if="status == 'idle' || status == 'pending'"
          >
            <h3>
              {{ t("title") }}
            </h3>

            <Transition name="fade" mode="out-in">
              <div class="form-container" v-if="status == 'idle'">
                <TextInput
                  ref="emailInput"
                  class="input-email"
                  :validation="emailValidation"
                  :validation-mode="'none'"
                  v-model="email"
                  :type="'email'"
                  :placeholder="t('input.email.placeholder')"
                />
                <CheckboxInput
                  ref="tcConsentInput"
                  class="input-tc"
                  v-model="tcConsent"
                  :validation="tcConsentValidation"
                >
                  <i18n-t keypath="input.tcConsent.label.text">
                    <NuxtLink href="/termos-condicoes-gerais" target="_blank">
                      {{ t("input.tcConsent.label.linkTc") }}
                    </NuxtLink>
                    <NuxtLink href="/politica-privacidade" target="_blank">
                      {{ t("input.tcConsent.label.linkPp") }}
                    </NuxtLink>
                  </i18n-t>
                </CheckboxInput>
                <div class="btn-container">
                  <CjaButton :color="'orange'" @click="submit">{{
                    t("btn.submit")
                  }}</CjaButton>
                </div>
              </div>
              <LoadingSpinner v-else />
            </Transition>
          </div>
          <div class="success-container" v-else-if="status == 'success'">
            <div class="success-wrapper">
              <div class="image-container">
                <StoryblokImg
                  :image="{
                    filename:
                      'https://a.storyblok.com/f/223704/96x84/aafed874b9/success-icon.svg',
                    alt: 'Sucesso',
                  }"
                />
              </div>
              <div class="text-container">
                <h4>
                  {{ t("success.title") }}
                </h4>
                <p>
                  {{ t("success.description") }}
                </p>
              </div>
            </div>
          </div>
        </Transition>
      </GridItem>
    </GridContainer>
  </div>
</template>

<script lang="ts" setup>
import {
  CheckboxInput,
  CjaButton,
  GridContainer,
  GridItem,
  LoadingSpinner,
  TextInput,
} from "cja-phoenix";

const { t } = useI18n();

const {
  submit,
  status,
  email,
  emailInput,
  emailValidation,
  tcConsent,
  tcConsentInput,
  tcConsentValidation,
} = useNewsletterSignup();
</script>

<style lang="scss" scoped>
.newsletter-signup {
  .newsletter-signup-wrapper {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px 32px;
    margin: 0 auto;

    @media screen and (min-width: $break-md-min) {
      grid-template-columns: 200px 1fr;
      gap: 64px;
    }

    > .image-container {
      img {
        display: block;
        width: 100%;
        max-width: 180px;
        margin: 0 auto;

        @media screen and (min-width: $break-xs-min) {
          max-width: 230px;
        }
      }
    }
  }

  h3 {
    font-size: 18px;
    line-height: 22px;
    font-weight: 700;
    text-align: center;
    margin: 0 0 16px;

    @media screen and (min-width: $break-md-min) {
      text-align: left;
    }
  }

  .form-container {
    display: grid;
    gap: 16px;
    width: 100%;
    max-width: 450px;

    @media screen and (min-width: $break-md-min) {
      grid-template-columns: 1fr auto;
      grid-template-rows: repeat(2, min-content);
    }

    .input-email {
      @media screen and (min-width: $break-md-min) {
        grid-area: 1 / 1 / 2 / 2;
      }
    }

    .input-tc {
      @media screen and (min-width: $break-md-min) {
        grid-area: 2 / 1 / 3 / 3;
      }
    }

    .btn-container {
      @media screen and (min-width: $break-md-min) {
        grid-area: 1 / 2 / 2 / 3;
      }
    }
  }

  .btn-container {
    display: grid;

    @media screen and (min-width: $break-md-min) {
      display: flex;
      justify-content: center;
      align-items: flex-start;
    }
  }

  .success-container {
    .success-wrapper {
      @media screen and (min-width: $break-xs-min) and (max-width: $break-sm-max) {
        display: grid;
        grid-template-columns: 60px 1fr;
        gap: 24px;
      }
      .image-container {
        margin: 0 auto 16px;

        @media screen and (min-width: $break-md-min) {
          margin: 0 0 16px;
        }

        img {
          display: block;
          max-width: 100%;
          height: 50px;
          max-width: 60px;
          margin: 0 auto;

          @media screen and (min-width: $break-md-min) {
            margin: 0;
          }
        }
      }

      .text-container {
        h4 {
          font-size: 16px;
          line-height: 20px;
          font-weight: 700;
          margin: 0 0 8px;

          @media screen and (min-width: $break-md-min) {
            font-size: 18px;
            line-height: 22px;
          }
        }

        p {
          font-size: 16px;
          line-height: 20px;
          white-space: pre-wrap;
          max-width: 500px;
          margin: 0 auto;
        }
      }
    }
  }

  @include fade-transition;
}
</style>

<i18n lang="json">
{
  "pt": {
    "title": "Aprende a poupar connosco!",
    "success": {
      "title": "O teu email foi submetido com sucesso!",
      "description": "Obrigado por subscreveres a newsletter. Receberás em breve as melhores dicas de poupança e acesso a ofertas exclusivas.\nBem-vindo à nossa comunidade!"
    },
    "input": {
      "email": {
        "placeholder": "O teu email"
      }
    },
    "btn": {
      "submit": "Receber"
    }
  }
}
</i18n>
